import { EarningsPeriod, Entity } from '@api';
import { Toggle } from '@design/components/toggle/Toggle';
import { CustomFilterModal } from '@terminal/components/custom-filter-modal/CustomFilterModal';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useFilterActions } from '@terminal/hooks/use-filter-actions/useFilterActions';
import { useTranslation } from 'react-i18next';

import { EarningsFilters } from '../../components/earnings-filters/EarningsFilters';
import { EarningsManualEditOptions } from '../../components/earnings-manual-edit-options/EarningsManualEditOptions';
import { EarningsPriceMovementsChartAccordion } from '../../components/earnings-price-movements-chart-accordion/EarningsPriceMovementsChartAccordion';
import { EarningsTableWrapper } from '../../components/earnings-table-wrapper/EarningsTableWrapper';
import styles from '../../Earnings.module.scss';
import { UnreportedEarnings } from '../../UnreportedEarnings';
import { useEarningsStore } from '../../useEarningsStore/useEarningsStore';

export type EarningsContentProps = {
  entity: Entity;
  latest?: EarningsPeriod;
};

export const EarningsContent = ({ entity, latest }: EarningsContentProps) => {
  const { t } = useTranslation([
    'widget',
    'asset-overview',
    'chat',
    'common',
    'screener',
    'scenario',
    'earnings',
    'analyze',
    'stream',
  ]);
  const {
    isViewSelectedOnly,
    rowSelection,
    rowSelectionByManualEdit,
    setIsViewSelectedOnly,
    onFilterGroupsChange,
    activeModalFilter,
    setActiveModalFilter,
  } = useEarningsStore();
  const { addFilterOption, addActiveFilters } = useFilterActions({
    store: useEarningsStore,
    group: 'EARNINGS_FILTERS',
    onFilterGroupsChange,
  });

  const hasManualChanges = !!Object.keys(rowSelectionByManualEdit).length;

  return (
    <div className={styles.root}>
      <UnreportedEarnings latest={latest} />
      <div className={styles.filterOptionsWrapper}>
        <Toggle
          className={styles.styledToggle}
          data-testid="view-selected-only-toggle"
          icon="Checkmark"
          isChecked={isViewSelectedOnly}
          onToggle={() => setIsViewSelectedOnly(!isViewSelectedOnly)}
          size="small"
          leftLabel={t('widget:earnings.viewSelectedOnly')}
        />
        <EarningsFilters />
      </div>
      {hasManualChanges && <EarningsManualEditOptions />}
      {!!Object.keys(rowSelection).length && (
        <ErrorBoundaryWrapper>
          <EarningsPriceMovementsChartAccordion
            rowSelection={rowSelection}
            entity={entity}
          />
        </ErrorBoundaryWrapper>
      )}
      <ErrorBoundaryWrapper showBorder={false}>
        <EarningsTableWrapper entity={entity} />
      </ErrorBoundaryWrapper>
      {activeModalFilter && (
        <CustomFilterModal
          onClose={() => setActiveModalFilter(null)}
          activeFilter={activeModalFilter}
          addFilterOption={addFilterOption}
          addActiveFilters={addActiveFilters}
        />
      )}
    </div>
  );
};
